import React, { useRef, useState, useEffect, useCallback } from 'react';
import * as S from './ShopGallery.styles';
import { ShopGalleryPreviewProps } from '@/api/types/shop.types';
import useScreenSize from '@/hooks/useScreenSize';
import { debounce } from '@/utils/debounce';
import { DEFAULT_SCROLL_STEP_PX } from '@/components/Shop/Shop.constants';

export const ShopGalleryPreview: React.FC<ShopGalleryPreviewProps> = ({
  items,
  onOpenGallery,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { isLarge } = useScreenSize();

  const checkScroll = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    setCanScrollLeft(wrapper.scrollLeft > 0);
    setCanScrollRight(
      wrapper.scrollLeft + wrapper.clientWidth < wrapper.scrollWidth
    );
  }, []);

  const scrollStepRef = useRef<number>(DEFAULT_SCROLL_STEP_PX as number);

  const scroll = (direction: 'left' | 'right') => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const step = scrollStepRef.current ?? DEFAULT_SCROLL_STEP_PX;
    const dx = direction === 'left' ? -step : step;
    wrapper.scrollBy({ left: dx, top: 0, behavior: 'smooth' });

    setTimeout(() => {
      checkScroll();
    }, 220);
  };

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    checkScroll();

    const computeStep = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const step = Math.min(
        DEFAULT_SCROLL_STEP_PX as number,
        Math.max(200, Math.round(el.clientWidth * 0.8))
      );
      scrollStepRef.current = step;
    };

    computeStep();

    const ro = new ResizeObserver(computeStep);
    ro.observe(wrapper);
    window.addEventListener('resize', computeStep);

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        checkScroll();
        ticking = false;
      });
    };

    const onResize = debounce(checkScroll, 150);

    wrapper.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      wrapper.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      ro.disconnect();
      window.removeEventListener('resize', computeStep);
    };
  }, [items.length, checkScroll]);

  if (!items.length) return null;

  return (
    <S.Gallery>
      <S.ThumbnailsScrollContainer>
        <S.GalleryWrapper ref={wrapperRef}>
          {items.map((item, idx) => (
            <S.GalleryThumbnail
              key={`${item.type}-${
                item.type === 'youtube' ? item.videoId : item.src
              }`}
              data-testid="shop-gallery-thumb"
              onClick={() => onOpenGallery(idx)}
            >
              <img
                src={
                  item.type === 'youtube'
                    ? `https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`
                    : item.src
                }
                alt={item.alt}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              {item.type === 'youtube' && <S.VideoIconOverlay />}
            </S.GalleryThumbnail>
          ))}
        </S.GalleryWrapper>
        {canScrollLeft && isLarge && (
          <S.ScrollButton $side="left" onClick={() => scroll('left')} />
        )}
        {canScrollRight && isLarge && (
          <S.ScrollButton $side="right" onClick={() => scroll('right')} />
        )}
      </S.ThumbnailsScrollContainer>
    </S.Gallery>
  );
};
